// Header chip for owner+manager: shows current plan + sites quota and
// trial-day countdown. Hidden for foreman/worker.
import React from "react";
import { Link } from "react-router-dom";
import { Timer, AlertTriangle, BarChart3 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { useAuth } from "../../contexts/AuthContext";
import { trialDaysLeft } from "../../lib/plans";
import { api } from "../../lib/api";

export default function PlanQuotaChip() {
  const { user, company } = useAuth();
  const visible = user?.role === "owner" || user?.role === "manager";
  const { data } = useQuery({
    queryKey: ["billing", "subscription"],
    queryFn: async () => (await api.get("/billing/subscription")).data,
    enabled: visible,
    staleTime: 30 * 1000,
    retry: false,
  });
  if (!visible || !user || !company) return null;

  const plan = data?.plan;
  const usage = data?.usage;
  const planName = plan?.name || "—";
  const maxSites = plan?.limits?.max_sites;
  const sites = usage?.sites;
  const isTrial = company.plan === "trial";
  const days = isTrial ? trialDaysLeft(company.trial_ends_at) : null;

  let label = `Plan ${planName}`;
  let tone = "bg-muted text-muted-foreground border-border";
  let icon = <BarChart3 className="h-3.5 w-3.5" />;

  if (isTrial && days !== null) {
    label = `Essai · J-${Math.max(days, 0)}`;
    if (days <= 0) {
      tone = "bg-red-50 text-red-700 border-red-200 animate-pulse";
      icon = <AlertTriangle className="h-3.5 w-3.5" />;
      label = "Essai expiré";
    } else if (days <= 3) {
      tone = "bg-red-50 text-red-700 border-red-200";
      icon = <AlertTriangle className="h-3.5 w-3.5" />;
    } else if (days <= 7) {
      tone = "bg-orange-50 text-orange-700 border-orange-200";
      icon = <Timer className="h-3.5 w-3.5" />;
    } else {
      tone = "bg-emerald-50 text-emerald-700 border-emerald-200";
      icon = <Timer className="h-3.5 w-3.5" />;
    }
  }

  // Append sites quota if known. -1 means unlimited.
  let quotaPart = "";
  let quotaReached = false;
  if (typeof maxSites === "number" && typeof sites === "number") {
    if (maxSites === -1) {
      quotaPart = ` · ${sites} chantiers`;
    } else {
      quotaPart = ` · ${sites}/${maxSites} chantiers`;
      if (sites >= maxSites) {
        tone = "bg-red-50 text-red-700 border-red-200";
        icon = <AlertTriangle className="h-3.5 w-3.5" />;
        quotaReached = true;
      }
    }
  }

  const linkTo = user.role === "owner" ? "/settings/billing" : "/help/plans";

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={linkTo}
            data-testid="plan-quota-chip"
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${tone} hover:opacity-90 transition`}
          >
            {icon}
            <span className="hidden md:inline">{label}{quotaPart}</span>
            <span className="md:hidden">
              {isTrial ? `J-${Math.max(days || 0, 0)}` : planName}
              {typeof sites === "number" && typeof maxSites === "number" && maxSites !== -1
                ? ` · ${sites}/${maxSites}`
                : ""}
            </span>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
          <p className="font-semibold mb-1">Plan {planName}</p>
          {isTrial && days !== null && (
            <p>Essai gratuit — {days > 0 ? `${days} jour${days > 1 ? "s" : ""} restant${days > 1 ? "s" : ""}` : "expiré"}.</p>
          )}
          {typeof sites === "number" && typeof maxSites === "number" && (
            <p>
              Chantiers : {sites} sur{" "}
              {maxSites === -1 ? "illimité" : maxSites}
              {quotaReached && " — limite atteinte"}
            </p>
          )}
          <p className="mt-1 text-muted-foreground">
            Cliquez pour {user.role === "owner" ? "gérer l'abonnement" : "comparer les plans"}.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
